import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline as DeleteIcon, GroupsOutlined as TeamIcon, PersonAddAlt1Outlined as AddPersonIcon, PersonRemoveOutlined as RemoveIcon } from '@mui/icons-material';
import SidePanel, { PanelSection } from '../SidePanel';
import { GREEN, GREEN_DARK, GREEN_SOFT, INK, LINE, MUTED } from '../home/homeTheme';
import {
  addProjectMember,
  addTeamMember,
  createTeam,
  deleteTeam,
  fetchAllUsers,
  removeProjectMember,
  removeTeamMember,
  setMemberTeam,
  updateMemberRole,
  updateTeam,
} from '../../utils/projectPeopleService';

// Colori dei ruoli di progetto (codici come nella pagina classica: S, J, T, C)
const ROLE_STYLE = {
  S: { bgcolor: 'var(--rt-forecast-soft)', color: 'var(--rt-forecast-ink)' },
  J: { bgcolor: GREEN_SOFT, color: GREEN_DARK },
  T: { bgcolor: 'rgba(142,107,191,0.16)', color: '#8E6BBF' },
  C: { bgcolor: 'var(--rt-track)', color: MUTED },
};

export function RoleChip({ code, name }) {
  return (
    <Box component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 999, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', ...(ROLE_STYLE[code] || ROLE_STYLE.C) }}>
      {name || 'No role'}
    </Box>
  );
}

const initials = (name) =>
  String(name || '?')
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('');

export function PersonAvatar({ name, size = 32 }) {
  return (
    <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: GREEN_SOFT, color: GREEN_DARK, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: size * 0.38, fontWeight: 800 }}>
      {initials(name)}
    </Box>
  );
}

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const saveButtonSx = { textTransform: 'none', fontWeight: 700, boxShadow: 'none', minWidth: 120, bgcolor: GREEN };
const shrink = (params) => ({ ...params.InputLabelProps, shrink: true });

function ConfirmDialog({ open, title, text, action, busy, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onClose={() => !busy && onCancel()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{text}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onCancel} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
          {busy ? <CircularProgress size={18} color="inherit" /> : action}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TeamSelect({ teams, value, onChange, disabled, helperText }) {
  return (
    <TextField
      select
      label="Team"
      size="small"
      fullWidth
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      helperText={helperText}
      // "No team" visibile anche come valore scelto
      SelectProps={{ displayEmpty: true }}
      InputLabelProps={{ shrink: true }}
    >
      <MenuItem value="">
        <em>No team</em>
      </MenuItem>
      {teams.map((t) => (
        <MenuItem key={t.id} value={t.id}>
          {t.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

function RoleSelect({ roles, value, onChange, disabled, error }) {
  return (
    <TextField select label="Role on the project" size="small" fullWidth value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} error={!!error} helperText={error}>
      {roles.map((r) => (
        <MenuItem key={r.id} value={r.id}>
          {r.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** Persona del progetto: ruolo e team modificabili, rimozione dal progetto. */
export function MemberPanel({ open, member, team, roles, teams, projectId, canEdit, onClose, onChanged }) {
  const [form, setForm] = useState({ roleId: '', teamId: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const userId = member?.user_id;
  useEffect(() => {
    if (!member) return;
    setForm({ roleId: member.project_role_id ?? '', teamId: team?.id ?? '' });
    setError(null);
    setConfirm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, team?.id, member?.project_role_id]);

  if (!member) return <SidePanel open={false} onClose={onClose} />;

  const dirty = String(form.roleId) !== String(member.project_role_id ?? '') || String(form.teamId) !== String(team?.id ?? '');
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (String(form.roleId) !== String(member.project_role_id ?? '')) await updateMemberRole(projectId, userId, form.roleId);
      await setMemberTeam(userId, team?.id, form.teamId || null);
      onChanged('Changes saved');
    } catch (e) {
      setError(`Not saved: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    setSaving(true);
    setError(null);
    try {
      await removeProjectMember(projectId, userId);
      setConfirm(false);
      onChanged('Removed from the project', { close: true });
    } catch (e) {
      setConfirm(false);
      setError(`Not removed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SidePanel
        open={open}
        onClose={onClose}
        busy={saving}
        icon={<PersonAvatar name={member.username} size={46} />}
        title={member.username}
        subtitle={member.email}
        footer={
          canEdit &&
          dirty && (
            <>
              <Button color="inherit" onClick={() => setForm({ roleId: member.project_role_id ?? '', teamId: team?.id ?? '' })} disabled={saving} sx={{ textTransform: 'none' }}>
                Discard
              </Button>
              <Button variant="contained" color="success" onClick={save} disabled={saving} sx={saveButtonSx}>
                {saving ? <CircularProgress size={18} color="inherit" /> : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        <PanelSection title="On this project">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.75 }}>
            <RoleSelect roles={roles} value={form.roleId} onChange={(roleId) => setForm((f) => ({ ...f, roleId }))} disabled={!canEdit} />
            <TeamSelect
              teams={teams}
              value={form.teamId}
              onChange={(teamId) => setForm((f) => ({ ...f, teamId }))}
              disabled={!canEdit}
              helperText="A person can be in one team of the project"
            />
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>Added on {fmtDate(member.created_at)}</Typography>
          </Box>
        </PanelSection>
        {error && <Alert severity="error">{error}</Alert>}
        {canEdit && (
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${LINE}` }}>
            <Button color="error" size="small" startIcon={<RemoveIcon />} onClick={() => setConfirm(true)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Remove from project
            </Button>
          </Box>
        )}
      </SidePanel>
      <ConfirmDialog
        open={confirm}
        title={`Remove ${member.username}?`}
        text="They will no longer be part of the project. Their worklogs stay as they are."
        action="Remove"
        busy={saving}
        onCancel={() => setConfirm(false)}
        onConfirm={remove}
      />
    </>
  );
}

/** Aggiunta di una persona al progetto, con ruolo e (facoltativo) team. */
export function AddMemberPanel({ open, members, roles, teams, projectId, onClose, onChanged }) {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ user: null, roleId: '', teamId: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const memberRole = roles.find((r) => r.code === 'C');
    setForm({ user: null, roleId: memberRole?.id ?? '', teamId: '' });
    setErrors({});
    fetchAllUsers()
      .then((list) => !cancelled && setUsers(list))
      .catch((e) => !cancelled && setErrors({ general: `Unable to load users: ${e.message}` }));
    return () => {
      cancelled = true;
    };
  }, [open, roles]);

  const available = useMemo(() => {
    const onProject = new Set(members.map((m) => String(m.user_id)));
    return (users || []).filter((u) => !onProject.has(String(u.id))).sort((a, b) => String(a.username).localeCompare(String(b.username)));
  }, [users, members]);

  const save = async () => {
    const e = {};
    if (!form.user) e.user = 'Choose a person';
    if (!form.roleId) e.roleId = 'Required';
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      await addProjectMember(projectId, form.user.id, form.roleId);
      if (form.teamId) await addTeamMember(form.teamId, form.user.id);
      onChanged(`${form.user.username} added`, { close: true });
    } catch (err) {
      setErrors({ general: `Not added: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      busy={saving}
      icon={
        <Box sx={{ width: 46, height: 46, borderRadius: 3, bgcolor: GREEN_SOFT, color: GREEN_DARK, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AddPersonIcon />
        </Box>
      }
      title="Add person"
      subtitle="Give someone access to this project"
      footer={
        <>
          <Button color="inherit" onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={save} disabled={saving || !users} sx={saveButtonSx}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Add person'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Autocomplete
          size="small"
          options={available}
          loading={!users}
          value={form.user}
          onChange={(e, user) => {
            setForm((f) => ({ ...f, user }));
            setErrors((x) => ({ ...x, user: undefined }));
          }}
          getOptionLabel={(u) => u.username || ''}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterOptions={(options, { inputValue }) => {
            const q = inputValue.trim().toLowerCase();
            return (q ? options.filter((u) => [u.username, u.email].some((v) => String(v || '').toLowerCase().includes(q))) : options).slice(0, 80);
          }}
          renderOption={(props, u) => (
            <li {...props} key={u.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <PersonAvatar name={u.username} size={26} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600 }}>
                    {u.username}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
                    {u.email}
                  </Typography>
                </Box>
              </Box>
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="Person" placeholder="Search name or email" autoFocus error={!!errors.user} helperText={errors.user} InputLabelProps={shrink(params)} />}
          noOptionsText="Everyone matching is already on the project"
        />
        <RoleSelect roles={roles} value={form.roleId} onChange={(roleId) => setForm((f) => ({ ...f, roleId }))} error={errors.roleId} />
        <TeamSelect teams={teams} value={form.teamId} onChange={(teamId) => setForm((f) => ({ ...f, teamId }))} helperText="Optional" />
        {errors.general && <Alert severity="error">{errors.general}</Alert>}
      </Box>
    </SidePanel>
  );
}

/** Team del progetto: nome e descrizione, membri (aggiunta e rimozione), eliminazione. Senza team crea un team nuovo. */
export function TeamPanel({ open, team, creating, members, teams, projectId, canEdit, onClose, onChanged }) {
  const [form, setForm] = useState({ name: '', description: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const teamId = team?.id;
  useEffect(() => {
    setForm(!creating && team ? { name: team.name || '', description: team.description || '' } : { name: '', description: '' });
    setErrors({});
    setConfirm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, creating, open]);

  const byUser = useMemo(() => Object.fromEntries(members.map((m) => [String(m.user_id), m])), [members]);
  // Si possono aggiungere solo persone del progetto che non sono già in un team
  const free = useMemo(() => {
    const inTeam = new Set(teams.flatMap((t) => t.members.map((m) => String(m.user_id))));
    return members.filter((m) => !inTeam.has(String(m.user_id))).sort((a, b) => String(a.username).localeCompare(String(b.username)));
  }, [members, teams]);

  if (!team && !creating) return <SidePanel open={false} onClose={onClose} />;

  const dirty = creating || form.name !== (team?.name || '') || form.description !== (team?.description || '');
  const run = async (action, message, options) => {
    setBusy(true);
    setErrors({});
    try {
      const res = await action();
      onChanged(message, typeof options === 'function' ? options(res) : options);
    } catch (e) {
      setErrors({ general: e.message });
    } finally {
      setBusy(false);
    }
  };
  const save = () => {
    if (!form.name.trim()) {
      setErrors({ name: 'Required' });
      return;
    }
    const data = { name: form.name.trim(), description: form.description.trim() };
    if (creating) run(() => createTeam(projectId, data), 'Team created', (res) => ({ openTeam: res?.team?.id ?? res?.id }));
    else run(() => updateTeam(team, data), 'Team updated');
  };
  const teamMembers = (team?.members || [])
    .map((m) => ({ userId: m.user_id, name: byUser[String(m.user_id)]?.username || m.user?.username || `User ${m.user_id}`, member: byUser[String(m.user_id)] }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <SidePanel
        open={open}
        onClose={onClose}
        busy={busy}
        icon={
          <Box sx={{ width: 46, height: 46, borderRadius: 3, bgcolor: GREEN_SOFT, color: GREEN_DARK, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TeamIcon />
          </Box>
        }
        title={creating ? 'New team' : team.name}
        subtitle={creating ? 'Group people of this project' : `${teamMembers.length} member${teamMembers.length === 1 ? '' : 's'}`}
        footer={
          canEdit &&
          dirty && (
            <>
              <Button color="inherit" onClick={creating ? onClose : () => setForm({ name: team.name || '', description: team.description || '' })} disabled={busy} sx={{ textTransform: 'none' }}>
                {creating ? 'Cancel' : 'Discard'}
              </Button>
              <Button variant="contained" color="success" onClick={save} disabled={busy} sx={saveButtonSx}>
                {busy ? <CircularProgress size={18} color="inherit" /> : creating ? 'Create team' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        <PanelSection title="Team">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.75 }}>
            <TextField
              label="Name"
              size="small"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              error={!!errors.name}
              helperText={errors.name}
              disabled={!canEdit}
              autoFocus={creating}
            />
            <TextField label="Description" size="small" multiline minRows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} disabled={!canEdit} />
          </Box>
        </PanelSection>

        {!creating && (
          <PanelSection title={`Members · ${teamMembers.length}`}>
            {canEdit && (
              <Autocomplete
                size="small"
                options={free}
                value={null}
                blurOnSelect
                clearOnBlur
                disabled={busy}
                onChange={(e, m) => m && run(() => addTeamMember(team.id, m.user_id), `${m.username} added to ${team.name}`)}
                getOptionLabel={(m) => m.username || ''}
                renderOption={(props, m) => (
                  <li {...props} key={m.user_id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography noWrap sx={{ fontSize: 13.5, flex: 1 }}>
                        {m.username}
                      </Typography>
                      <RoleChip code={m.role_code} name={m.role_name} />
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} placeholder={free.length ? 'Add a person of the project…' : 'Everyone is already in a team'} />}
                noOptionsText="Everyone is already in a team"
                sx={{ mb: 1 }}
              />
            )}
            {!teamMembers.length ? (
              <Typography sx={{ fontSize: 13.5, color: MUTED }}>No members yet.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {teamMembers.map((m) => (
                  <Box key={m.userId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6 }}>
                    <PersonAvatar name={m.name} size={28} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>
                        {m.name}
                      </Typography>
                      {!m.member && <Typography sx={{ fontSize: 12, color: MUTED }}>No longer on the project</Typography>}
                    </Box>
                    {m.member && <RoleChip code={m.member.role_code} name={m.member.role_name} />}
                    {canEdit && (
                      <Tooltip title="Remove from the team">
                        <span>
                          <IconButton size="small" disabled={busy} onClick={() => run(() => removeTeamMember(team.id, m.userId), `${m.name} removed from ${team.name}`)}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </PanelSection>
        )}

        {errors.general && <Alert severity="error">{errors.general}</Alert>}

        {!creating && canEdit && (
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${LINE}` }}>
            <Button color="error" size="small" startIcon={<DeleteIcon />} onClick={() => setConfirm(true)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Delete team
            </Button>
          </Box>
        )}
      </SidePanel>
      {team && (
        <ConfirmDialog
          open={confirm}
          title={`Delete ${team.name}?`}
          text={teamMembers.length ? `Its ${teamMembers.length} member${teamMembers.length === 1 ? '' : 's'} will stay on the project, without a team.` : 'The team has no members.'}
          action="Delete"
          busy={busy}
          onCancel={() => setConfirm(false)}
          onConfirm={() => run(() => deleteTeam(team.id), 'Team deleted', { close: true })}
        />
      )}
    </>
  );
}
